import { IntlFormatters } from 'react-intl';

import { URLRegEx } from '@marketplace/common';

import { TAPICountry, TStatesCode } from '@marketplace/api';

import Joi from 'joi';

type TValidationSchemeOptions = {
  formatMessage: IntlFormatters['formatMessage'];
  countries: TAPICountry[];
};

export const partnerProfileValidationSchema = ({
  formatMessage,
  countries,
}: TValidationSchemeOptions) => {
  const companyNameLabel = formatMessage({
    id: 'developer_personal_page_partner_profile_company_name',
  });
  const countryLabel = formatMessage({
    id: 'developer_personal_page_partner_profile_country',
  });
  const cityLabel = formatMessage({
    id: 'developer_personal_page_partner_profile_city',
  });
  const postalCodeLabel = formatMessage({
    id: 'developer_personal_page_partner_profile_postal_code',
  });
  const websiteLabel = formatMessage({
    id: 'developer_personal_page_partner_profile_website',
  });
  const descriptionLabel = formatMessage({
    id: 'developer_personal_page_partner_profile_description',
  });
  const adressLabel = formatMessage({
    id: 'developer_personal_page_partner_profile_address',
  });
  const stateLabel = formatMessage({
    id: 'developer_personal_page_partner_profile_state',
  });

  const countriesWithStates = countries.filter(
    (country: any) => Object.values(TStatesCode).indexOf(country.key) >= 0
  );

  const stateValidationRules = countriesWithStates.map((country) => {
    return {
      is: Joi.object(country),
      then: Joi.object()
        .required()
        .messages({
          'any.required': formatMessage(
            { id: 'generalForm_validation_required' },
            { field: stateLabel }
          ),
        }),
    };
  });

  return Joi.object({
    company_name: Joi.string()
      .trim()
      .max(100)
      .messages({
        'string.max': formatMessage(
          { id: 'generalForm_validation_range' },
          { field: companyNameLabel, min: 1, max: 100 }
        ),
      })
      .allow(''),
    logo: Joi.optional(),
    icon: Joi.optional(),
    address: Joi.string()
      .trim()
      .max(2000)
      .messages({
        'string.max': formatMessage(
          { id: 'generalForm_validation_range' },
          { field: adressLabel, min: 0, max: 2000 }
        ),
      })
      .allow(''),
    website: Joi.string()
      .trim()
      .allow('')
      .max(2000)
      .pattern(URLRegEx)
      .messages({
        'string.pattern.base': formatMessage(
          { id: 'generalForm_validation_uri' },
          { field: websiteLabel }
        ),
        'string.max': formatMessage(
          { id: 'generalForm_validation_range' },
          { field: websiteLabel, min: 1, max: 2000 }
        ),
      }),
    country: Joi.object()
      .required()
      .messages({
        'any.required': formatMessage(
          { id: 'generalForm_validation_required' },
          { field: countryLabel }
        ),
      }),
    state: stateValidationRules?.length
      ? Joi.object().when('country', {
          switch: stateValidationRules,
          otherwise: Joi.object().allow(''),
        })
      : Joi.object().allow(''),
    // eslint-disable-next-line
    city: Joi.string()
      .trim()
      .required()
      .max(100)
      .messages({
        'any.required': formatMessage(
          { id: 'generalForm_validation_required' },
          { field: cityLabel }
        ),
        'string.empty': formatMessage(
          { id: 'generalForm_validation_required' },
          { field: cityLabel }
        ),
        'string.max': formatMessage(
          { id: 'generalForm_validation_range' },
          { field: cityLabel, min: 1, max: 100 }
        ),
      }),
    // eslint-disable-next-line
    postal_code: Joi.string()
      .trim()
      .max(20)
      .messages({
        'string.max': formatMessage(
          { id: 'generalForm_validation_range' },
          { field: postalCodeLabel, min: 1, max: 20 }
        ),
      })
      .allow(''),
    applications_description: Joi.string()
      .max(1000)
      .required()
      .messages({
        'any.required': formatMessage(
          { id: 'generalForm_validation_required' },
          { field: descriptionLabel }
        ),
        'string.empty': formatMessage(
          { id: 'generalForm_validation_required' },
          { field: descriptionLabel }
        ),
        'string.pattern.base': formatMessage(
          { id: 'generalForm_validation_allowed' },
          { field: descriptionLabel }
        ),
        'string.max': formatMessage(
          { id: 'generalForm_validation_range' },
          { field: descriptionLabel, min: 1, max: 1000 }
        ),
      }),
  });
};
